/* eslint-disable @typescript-eslint/camelcase */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { FiMail, FiKey, FiLock } from 'react-icons/fi';
import { FaArrowLeft } from 'react-icons/fa';
import { Link, useLocation, useParams } from 'react-router-dom';

import ReCAPTCHA from 'react-google-recaptcha';

import { useAuth } from '../../../../hooks/Auth';
import { useToast } from '../../../../hooks/Toast';
import { useLanguage } from '../../../../hooks/Language';
import { useConfiguration } from '../../../../hooks/Configuration';

import Input from '../../../../components/Forms/Input';
import Button from '../../../../components/Button';
import Loader from '../../../../components/Loader';

import api from '../../../../services/api';
import getValidationErrors from '../../../../utils/getValidationErrors';
import { defaultProjectHash, recaptchaKey } from '../../../../config';

import { AnimatedDiv } from '../../styles';
import { LoginContent } from './styles';
import { ActiveButton } from '../../index';

import CadastroUnico from './CadastroUnico';

declare const window: any;

interface SignInData {
  email: string;
  password: string;
}

const LoginContainer: React.FC = () => {
  const query = new URLSearchParams(useLocation().search);

  const { signIn } = useAuth();
  const { addToast } = useToast();
  const { translate } = useLanguage();
  const { eventHash, configuration } = useConfiguration();
  const { projectHash = defaultProjectHash, product_hash } =
    useParams<Record<string, any>>();

  const formRef = useRef<FormHandles>(null);
  const recaptchaRef = useRef<any>(null);

  const [buyType, setBuyType] = useState<'' | 'new' | 'login'>('');
  const [loading, setLoading] = useState(false);
  const [captKey, setCaptKey] = useState(1);
  const [product, setProduct] = useState<Record<string, any>>({});

  /* -----------------------------------------------------------------
   * LOGIN
   * ----------------------------------------------------------------- */
  const handleLogin = useCallback(
    async (data: SignInData) => {
      setLoading(true);
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          email: Yup.string()
            .required(`${translate('Email')} ${translate('obrigatório')}`)
            .email(`${translate('Email')} ${translate('obrigatório')}`),
          password: Yup.string().required(
            `${translate('Senha')} ${translate('obrigatório')}`,
          ),
        });

        await schema.validate(data, { abortEarly: false });

        const { email, password } = data;
        await signIn({
          email,
          password,
          project_id: projectHash,
          event_id: eventHash,
        });

        window.location.href = eventHash
          ? `/app/${projectHash}/v2/event/${eventHash}/pagamento/${product?.hash_link}`
          : `/app/${projectHash}/v2/pagamento/${product?.hash_link}`;
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        }
        addToast({
          type: 'error',
          title: translate('Erro na autenticação'),
          description: translate('Email ou senha inválidos'),
        });
      } finally {
        setLoading(false);
      }
    },
    [signIn, addToast, translate, projectHash, eventHash, product],
  );

  /* -----------------------------------------------------------------
   * CADASTRO (usado em buyType === 'new')
   * Toda a lógica de validação/renderização agora vive no CadastroUnico.
   * Aqui só definimos os campos, o onSubmit específico e o reCAPTCHA.
   * ----------------------------------------------------------------- */
  const handleNewSignup = useCallback(
    async (data: Record<string, any>) => {
      const captcha = recaptchaRef.current?.getValue() || false;

      // Compatibilidade com o backend: se usuário escolheu "Outra",
      // o texto livre vai no próprio campo know_about.
      if (data.know_about === 'Outra' && data.know_about_other) {
        data.know_about = data.know_about_other;
      }
      delete data.know_about_other;

      // birth_date_text (compat com código antigo, caso o backend use)
      if (data.birth_date) data.birth_date_text = String(data.birth_date);

      try {
        const newUser = await api.post('/signup', {
          ...data,
          project_id: projectHash,
          event_id: eventHash,
          captcha,
        });

        if (!newUser) {
          setCaptKey(s => s + 1);
          return;
        }

        await signIn({ email: data.email, password: data.password });

        if (window?.fbq) {
          window.fbq('track', 'CompleteRegistration');
        }

        if (newUser.data?.confirmationCode) {
          window.location.href = eventHash
            ? `/app/${projectHash}/v2/event/${eventHash}/confirmacao/${newUser.data.confirmationCode}`
            : `/app/${projectHash}/v2/confirmacao/${newUser.data.confirmationCode}`;
        } else {
          window.location.href = eventHash
            ? `/app/${projectHash}/v2/event/${eventHash}/pagamento/${product?.hash_link}`
            : `/app/${projectHash}/v2/pagamento/${product?.hash_link}`;
        }
      } catch (err: any) {
        setCaptKey(s => s + 1);
        addToast({
          type: 'error',
          title:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            translate('Erro ao gerar seu cadastro'),
        });
      }
    },
    [signIn, addToast, translate, projectHash, eventHash, product],
  );

  /* -----------------------------------------------------------------
   * Produto
   * ----------------------------------------------------------------- */
  const loadProduct = useCallback(async () => {
    if (product_hash === 'courtesy') {
      setProduct({ title: 'Cortesia', hash_link: 'courtesy' });
      return;
    }
    try {
      const response = await api.get(`/product/${product_hash}/${projectHash}`);
      setProduct({ ...response.data, quantity: 1 });
    } catch (err) {
      addToast({
        title: translate('Erro ao identificar produto'),
        type: 'error',
      });
    }
  }, [product_hash, projectHash, addToast, translate]);

  useEffect(() => {
    if (configuration && product_hash) loadProduct();
  }, [configuration, product_hash, loadProduct]);

  /* -----------------------------------------------------------------
   * Render
   * ----------------------------------------------------------------- */
  return (
    <AnimatedDiv visible={true}>
      <LoginContent>
        {loading && <Loader message="Carregando" />}

        <div style={{ width: '100%', justifyContent: 'center' }}>
          <aside style={{ width: '100%', maxWidth: '100%' }}>
            {/* ---------- Tela inicial: escolher novo cadastro OU fazer login ---------- */}
            {buyType === '' && (
              <AnimatedDiv visible style={{ paddingTop: 40 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <p
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: '#333',
                      marginBottom: 20,
                    }}
                    onClick={() => {
                      window.location.href = `/app/${projectHash}/v2/inscricao`;
                    }}
                  >
                    <FaArrowLeft color="#333" /> {translate('Voltar')}
                  </p>

                  <ActiveButton step={11} />

                  {product.title && (
                    <aside
                      style={{
                        fontSize: 14,
                        lineHeight: '18px',
                        maxWidth: 350,
                        margin: '15px auto',
                        color: '#333',
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      <strong>{product.title}</strong>
                    </aside>
                  )}

                  <button
                    className="defaultButton"
                    style={{
                      margin: 15,
                      width: '100%',
                      maxWidth: 340,
                      minHeight: 50,
                      padding: 10,
                    }}
                    onClick={() => setBuyType('new')}
                  >
                    {translate('Novo Cadastro')}
                  </button>

                  <p
                    style={{
                      width: '100%',
                      background: '#333',
                      height: 2,
                      position: 'relative',
                      margin: '20px 0',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%) translateY(-20px)',
                        fontSize: 12,
                        background: '#333',
                        color: '#fff',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {translate('OU')}
                    </span>
                  </p>

                  <Form
                    className="form"
                    style={{ margin: 15 }}
                    ref={formRef}
                    onSubmit={handleLogin}
                  >
                    <h2 style={{ marginBottom: 20 }}>{translate('Faça seu login')}</h2>

                    <Input icon={FiMail} name="email" placeholder={translate('E-mail')} />
                    <Input
                      icon={FiKey}
                      type="password"
                      name="password"
                      placeholder={translate('Senha')}
                    />

                    <aside
                      style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'flex-end',
                        marginTop: 20,
                      }}
                    >
                      <button
                        className="defaultButton"
                        style={{ padding: 10, margin: '10px auto', width: 200 }}
                        type="submit"
                      >
                        {translate('LOGIN')}
                      </button>
                    </aside>

                    <aside style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <Link className="underline" style={{ marginTop: 10 }} to="/recuperar_senha">
                        {translate('Esqueceu a senha? Clique aqui!')}
                      </Link>
                    </aside>
                  </Form>
                </div>
              </AnimatedDiv>
            )}

            {/* ---------- Login alternativo (rota antiga, mantido por compatibilidade) ---------- */}
            {buyType === 'login' && (
              <AnimatedDiv visible>
                <Form className="form" ref={formRef} onSubmit={handleLogin}>
                  <h1 className="defaultMarked">
                    <div />
                    {translate('LOGIN')}
                  </h1>
                  <Input icon={FiMail} name="email" placeholder={translate('E-mail')} />
                  <Input
                    icon={FiLock}
                    name="password"
                    type="password"
                    placeholder={translate('Senha')}
                  />
                  <Button type="submit">{translate('Entrar')}</Button>
                  <Link style={{ color: '#333' }} to="/recuperar_senha">
                    {translate('Esqueci minha senha')}
                  </Link>
                  <p
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: '#333',
                      marginTop: 20,
                    }}
                    onClick={() => setBuyType('')}
                  >
                    <FaArrowLeft color="#333" /> {translate('Voltar')}
                  </p>
                </Form>
              </AnimatedDiv>
            )}

            {/* ---------- NOVO CADASTRO (tela única via CadastroUnico) ---------- */}
            {buyType === 'new' && (
              <AnimatedDiv className='column' visible>

                <div className='full-width'>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                  <ActiveButton step={2} />
                </div>

                <p
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: 12,
                    color: '#333',
                    margin: '10px 0 20px',
                  }}
                  onClick={() => setBuyType('')}
                >
                  <FaArrowLeft color="#333" /> {translate('Voltar')}
                </p>
</div>
                <CadastroUnico
                  title={translate('Novo cadastro')}
                  submitLabel={translate('Continuar')}
                  onSubmit={handleNewSignup}
                  fields={[
                    // Identificação
                    'country',
                    'name',
                    'social_name',
                    'phone',

                    // Documento
                    'document_type',
                    'document_number',
                    'birth_date',
                    'rg',
                    'gender_id',
                   // 'mother_name',
                  //  'father_name',
                   'nationality',
                  //  'born_in',

                    // Acesso
                    'email',
                    'password',

                    // Endereço
                  //  'zipcode',
                  //  'address',
                  //  'address_number',
                  //  'address_number_extra',
                  //  'neighborhood',
                    'city',
                    'state',

                    // Extras
                    'know_about',
                    'know_about_other', // condicional: só aparece quando know_about === 'Outra'
                    'term',
                  ]}
                  groupLabels={{
                    personal: translate('Dados pessoais'),
                    auth: translate('Crie seu acesso'),
                    address: translate('Meu endereço'),
                    extra: translate('Informações adicionais'),
                    terms: translate('Comunicações'),
                  }}
                  beforeSubmit={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        margin: '10px 0',
                      }}
                    >
                      <ReCAPTCHA
                        key={`recapt-${captKey}`}
                        ref={recaptchaRef}
                        sitekey={recaptchaKey}
                      />
                    </div>
                  }
                />
              </AnimatedDiv>
            )}
          </aside>
        </div>
      </LoginContent>
    </AnimatedDiv>
  );
};

export default LoginContainer;
